import React, { useState } from 'react';
import { useAccount } from '../../hooks/useAccount';
import { useChangeSubscription, useCheckoutFlow } from '../../hooks/useAccountMutations';
import { useProducts } from '../../hooks/useProducts';
import { Account } from '../../services/models/account';
import { Product } from '../../services/models/product';
import { SubscriptionInterval } from '../../services/models/subscription';
import { launchIntercom } from '../../utils/Intercom';
import LoadingSpinner from '../loading-spinner/LoadingSpinner';
import PeriodicitySwitch from '../periodicity-switch/PeriodicitySwitch';
import ProductCard from '../product-card/ProductCard';

export default function Pricing() {
  const { account: currentAccount } = useAccount();
  const account: Account = currentAccount!;
  const { changeSubscription } = useChangeSubscription();
  const { initCheckoutFlow } = useCheckoutFlow();
  const currentIsBillingMonthly = account?.subscription?.interval !== SubscriptionInterval.Yearly;
  const currentProductId = account?.product?.productId;
  const email = account?.email?.emailAddress;
  const { products, loading: isLoadingProducts } = useProducts();

  const [isBilledMonthly, setIsBilledMonthly] = useState(currentIsBillingMonthly);
  const [planRequiringConfirmation, setPlanRequiringConfirmation] = useState('');
  const [planRequiringLoading, setPlanRequiringLoading] = useState('');

  const handleTogglePeriodicity = () => {
    if (!currentIsBillingMonthly) {
      return;
    }

    setIsBilledMonthly(!isBilledMonthly);
  };

  const handleProductPriceSelection = (planId: string, product: Product) => {
    const userId = account?.userid;
    if (!userId) return;

    setPlanRequiringLoading(product.productId);

    if (account?.subscription?.planId && !account?.subscription?.canceled) {
      changeSubscription(planId);
    } else {
      initCheckoutFlow(planId);
    }
  };

  if (isLoadingProducts) {
    return <LoadingSpinner />;
  }

  return (
    <div className="w-full mt-6 sm:flex sm:flex-col sm:align-center">
      <h2 className="text-3xl font-extrabold text-gray-900 sm:text-center">
        Choose the plan that works for you
      </h2>

      <PeriodicitySwitch isToggled={isBilledMonthly} onChange={handleTogglePeriodicity} />

      <div className="mt-4 space-y-8 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-6 md:max-w-4xl md:mx-auto md:max-w-none lg:grid-cols-3">
        {products
          ?.filter(p => p.published || p.productId === currentProductId)
          .map(product => (
            <ProductCard
              key={product.productId}
              account={account}
              active={currentProductId === product.productId}
              mostPopular={currentProductId === product.productId}
              product={product}
              isBilledMonthly={isBilledMonthly}
              onSelected={handleProductPriceSelection}
              showsConfirmation={planRequiringConfirmation === product.productId}
              showsLoading={planRequiringLoading === product.productId}
              handleConfirmationChange={(confirmation: boolean) => {
                if (confirmation) {
                  setPlanRequiringConfirmation(product.productId);
                } else {
                  setPlanRequiringConfirmation('');
                }
              }}
            />
          ))}
      </div>

      <div className="hidden mt-8 mx-auto md:block">
        <div className="rounded-lg bg-gray-100 px-6 py-8 sm:p-10 lg:flex lg:items-center">
          <div className="flex-1">
            <div className="mt-4 text-gray-600 text-lg">
              Looking for API access, bigger plans, enterprise or custom solutions?
            </div>
          </div>
          <div className="mt-6 rounded-md shadow-sm lg:mt-0 lg:ml-10 lg:shrink-0">
            <button
              onClick={launchIntercom}
              className="flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-gray-900 bg-white hover:bg-gray-50"
            >
              Contact us
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
